import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Field } from '@/components/ui/field';
import { fullName, apiMessage } from '../lib';
import type { ReportSchedule } from '../reporting-types';
import { addScheduleRecipients, removeScheduleRecipient } from '../reportingApi';
import { StaffMultiSelect } from './StaffMultiSelect';

/**
 * Recipients are re-checked against the report they will get the moment they
 * are added (ADR-0013 §2.7) — a user missing a permission the report needs is
 * refused here with the reason, not silently dropped at 6am on delivery.
 */
export function RecipientsDialog({
  schedule,
  onClose,
  onChanged,
}: {
  schedule: ReportSchedule | null;
  onClose: () => void;
  onChanged: (schedule: ReportSchedule) => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('reports.schedule');
  const [adding, setAdding] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!schedule) return null;

  const existingIds = new Set(schedule.recipients.map((r) => r.userId));

  const add = async () => {
    const toAdd = adding.filter((id) => !existingIds.has(id));
    if (!toAdd.length) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await addScheduleRecipients(schedule.id, toAdd);
      onChanged(updated);
      setAdding([]);
      toast.success(t('reporting.schedules.recipientsAdded', { count: toAdd.length }));
    } catch (err) {
      setError(apiMessage(err, t('reporting.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (userId: string) => {
    setRemoving(userId);
    try {
      const updated = await removeScheduleRecipient(schedule.id, userId);
      onChanged(updated);
    } catch (err) {
      toast.error(apiMessage(err, t('reporting.errors.saveFailed')));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('reporting.schedules.recipients')}
      description={schedule.name}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        {schedule.recipients.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('reporting.schedules.noRecipients')}</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {schedule.recipients.map((recipient) => (
              <li key={recipient.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{fullName(recipient.user)}</span>{' '}
                  <span className="text-ink-muted">{recipient.user.email}</span>
                </span>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => remove(recipient.userId)}
                    disabled={removing === recipient.userId}
                    aria-label={t('common.remove')}
                    className="shrink-0 rounded p-1 text-ink-muted hover:bg-hover hover:text-red-600 disabled:opacity-40"
                  >
                    <Icon as={Trash2} size="sm" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <>
            <Field label={t('reporting.schedules.addRecipients')}>
              <StaffMultiSelect selected={adding} onChange={setAdding} />
            </Field>
            <Button onClick={add} disabled={saving || !adding.length}>
              {t('common.add')}
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}
